import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';
import { useMutation, useQuery } from '@tanstack/react-query';

export type TAgent = {
  id: number;
  name: string;
  jurisdictions: string[];
  updated_at: string;
  acceptsFiat: boolean;
  acceptsCrypto: boolean;
};

export type TAgentService = {
  id: number;
  agentId: number;
  agentName?: string;
  name: string;
  cogs: number;
  currency: string;
  updatedAt: Date;
  description: string;
  deliverables: {
    title: string;
    description?: string;
  }[];
};

export const keys = {
  all: ['agent'] as const,
  details: (agentId: number) => [keys.all, agentId, 'details'] as const,
  services: (agentId: number) => [keys.all, agentId, 'services'] as const,
  service: (serviceId: number) => [keys.all, 'service', serviceId] as const,
  included: (serviceId: number) =>
    [keys.all, 'included', serviceId, 'services'] as const,
};

export const useAgentServices = (agentId: number) => {
  return useQuery<TAgentService[]>({
    queryFn: () =>
      call<TAgentService[]>('agent/services/list', {
        json: { agentId },
      }).then((res) => res.json()),
    queryKey: keys.services(agentId),
    select: (data) =>
      data.map((d) => {
        d.updatedAt = new Date(d.updatedAt);
        return d;
      }),
    enabled: agentId > 0,
  });
};

export const useAddAgentService = (agentId: number) => {
  return useMutation<
    TAgentService,
    Error,
    Pick<TAgentService, 'name' | 'cogs' | 'currency'>
  >({
    mutationFn: (service: Pick<TAgentService, 'name' | 'cogs' | 'currency'>) =>
      call<any>('agent/services/add', {
        json: { agentId: agentId, service },
      }).then((res) => res.json()),
    onSuccess: (addedService: TAgentService) => {
      queryClient.setQueryData(
        keys.services(agentId),
        (existing: TAgentService[]) => [...existing, addedService],
      );
    },
  });
};

export const useUpdateAgentService = <T extends TAgentService>() => {
  return useMutation<T, Error, T>({
    mutationFn: async (service: T) => {
      const { updatedAt: _, ...json } = service;
      const res = await call<T>('agent/services/update', {
        json: json as any,
      });
      return res.json();
    },
    onSuccess: (updatedService: T) => {
      queryClient.setQueryData(keys.service(updatedService.id), updatedService);
      queryClient.setQueryData(
        keys.services(updatedService.agentId),
        (existing: TAgentService[]) =>
          existing.map((e) =>
            e.id === updatedService.id ? updatedService : e,
          ),
      );
    },
  });
};

export const useAgents = () =>
  useQuery<TAgent[]>({
    queryFn: () => call<TAgent[]>('agent/list').then((res) => res.json()),
    queryKey: keys.all,
  });

export const useAgent = (id: number) => {
  return useQuery<TAgent>({
    queryFn: () =>
      call<TAgent>('agent/details', { json: { id } }).then((res) => res.json()),
    queryKey: keys.details(id),
    enabled: id > 0,
  });
};

export const useUpdateAgentDetails = (id: number) => {
  return useMutation<
    TAgent,
    Error,
    Partial<
      Pick<TAgent, 'name' | 'jurisdictions' | 'acceptsFiat' | 'acceptsCrypto'>
    >
  >({
    mutationFn: (futureDetails) =>
      call<TAgent>('agent/update-details', {
        json: { id, ...futureDetails },
      }).then((res) => res.json()),
    onSuccess: (updatedDetails: TAgent) => {
      queryClient.setQueryData(keys.details(id), () => ({
        ...updatedDetails,
      }));
    },
  });
};

export const useAddAgent = () => {
  return useMutation<TAgent, Error, string>({
    mutationFn: (name: string) =>
      call<TAgent>('agent/add', { json: { name } }).then((res) => res.json()),
    onSuccess: (addedAgent: TAgent) => {
      queryClient.invalidateQueries({
        queryKey: keys.all,
      });
      queryClient.setQueryData(keys.all, (existing: TAgent[]) => [
        ...existing,
        addedAgent,
      ]);
    },
  });
};

export const useDeleteAgentService = (agentId: number) => {
  return useMutation({
    mutationFn: (serviceId: number) =>
      call('agent/services/delete', {
        json: { agentId, serviceId },
      }),
    onSuccess: (_, serviceId) => {
      queryClient.setQueryData(
        keys.services(agentId),
        (existing: TAgentService[]) =>
          existing.filter((e) => e.id !== serviceId),
      );
    },
  });
};
